import { useEffect, useState, useRef } from "react";
import axios from "axios";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Bot, Upload, Plus, Play, Trash2, Loader2, FileText, Clock, RefreshCw, CheckCircle2, AlertCircle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intervals = [
  { value: 5, label: "Every 5 min" },
  { value: 15, label: "Every 15 min" },
  { value: 30, label: "Every 30 min" },
  { value: 60, label: "Every hour" },
  { value: 120, label: "Every 2 hours" },
  { value: 240, label: "Every 4 hours" },
  { value: 1440, label: "Daily" },
];

const regions = ["global", "us", "eu", "uk", "mena", "asia", "africa", "latam"];

const AdminScheduler = () => {
  const { token } = useAuth();
  const { currentProject } = useProject();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [titles, setTitles] = useState([]);
  const [titleInput, setTitleInput] = useState("");
  const [prompt, setPrompt] = useState("");
  const [interval, setIntervalMin] = useState(60);
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("global");
  const [includeFaq, setIncludeFaq] = useState(true);
  const [includeImage, setIncludeImage] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const fileRef = useRef(null);

  const categories = currentProject?.categories || [];

  const loadJobs = async () => {
    if (!currentProject) return;
    setLoadingJobs(true);
    try {
      const r = await axios.get(`${API}/projects/${currentProject.id}/scheduler/jobs`, auth);
      setJobs(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    loadJobs();
    const t = setInterval(loadJobs, 15000);     // auto-refresh every 15s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id]);

  useEffect(() => {
    if (categories.length && !category) setCategory(categories[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length]);

  const addTitleManual = () => {
    const t = titleInput.trim();
    if (!t) return;
    setTitles([...titles, t]);
    setTitleInput("");
  };

  const removeTitle = (i) => {
    const next = [...titles]; next.splice(i, 1); setTitles(next);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || "");
      const rows = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      // Take first column (split by comma, ignore quoted-comma for simplicity)
      const newTitles = rows.map((r) => r.split(",")[0].replace(/^"|"$/g, "").trim()).filter(Boolean);
      // Drop header if it looks like one
      if (newTitles.length && /^(title|titles|name|topic)$/i.test(newTitles[0])) newTitles.shift();
      setTitles([...titles, ...newTitles]);
      toast.success(`Imported ${newTitles.length} title${newTitles.length === 1 ? "" : "s"}`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const launch = async () => {
    if (!currentProject) return;
    if (titles.length === 0) { toast.error("Add at least one title"); return; }
    setLaunching(true);
    try {
      await axios.post(`${API}/projects/${currentProject.id}/scheduler/jobs`, {
        titles, prompt, category, region,
        interval_minutes: interval,
        include_faq: includeFaq,
        include_image: includeImage,
      }, auth);
      toast.success(`Launched! ${titles.length} articles scheduled.`);
      setTitles([]);
      setPrompt("");
      await loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Launch failed");
    } finally {
      setLaunching(false);
    }
  };

  const cancelJob = async (jobId) => {
    if (!window.confirm("Cancel all pending items in this job?")) return;
    try {
      await axios.delete(`${API}/scheduler/jobs/${jobId}`, auth);
      toast.success("Job cancelled");
      await loadJobs();
    } catch (e) {
      toast.error("Cancel failed");
    }
  };

  if (!currentProject) {
    return (
      <AdminLayout title="Article Scheduler">
        <p className="text-slate-600">Select a project in the top dropdown to schedule articles.</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Article Scheduler — ${currentProject.name}`}>
      <div className="space-y-6" data-testid="admin-scheduler-page">
        {/* Hero */}
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shrink-0">
              <Bot className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">AI Content Monster</h2>
              <p className="text-sm text-slate-600 mt-1">Upload titles, set a prompt, schedule auto-publishing. Each title is generated as a full SEO article with optional FAQ + cover image, then auto-published to <span className="font-medium">{currentProject.name}</span>.</p>
            </div>
          </CardContent>
        </Card>

        {/* Step 1 — Titles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-blue-500" />
              1. Import Titles
              <span className="ml-auto text-xs font-normal text-slate-500" data-testid="titles-count">{titles.length} loaded</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsvUpload} data-testid="csv-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-csv-btn">
                <Upload className="h-4 w-4 mr-2" />
                Upload CSV
              </Button>
              <div className="flex-1 flex gap-2 min-w-[260px]">
                <Input
                  placeholder="Add a title manually…"
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTitleManual()}
                  data-testid="title-manual-input"
                />
                <Button variant="outline" onClick={addTitleManual} data-testid="add-title-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              {titles.length > 0 && (
                <Button variant="ghost" onClick={() => setTitles([])} data-testid="clear-titles-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Clear all
                </Button>
              )}
            </div>
            {titles.length > 0 && (
              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                {titles.map((t, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50" data-testid={`title-row-${i}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="text-xs font-mono text-slate-400 w-6">#{i + 1}</span>
                      <span className="truncate text-sm">{t}</span>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeTitle(i)} aria-label="Remove">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500">CSV format: one title per row, first column. Header row "title" is auto-skipped.</p>
          </CardContent>
        </Card>

        {/* Step 2 — Prompt */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="h-5 w-5 text-purple-500" />
              2. Content Prompt <span className="text-xs font-normal text-slate-400">(optional)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Optional — e.g. Write in a professional tone targeting business owners expanding into the EU. Include 2026 regulatory changes and cite primary sources."
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="prompt-input"
            />
          </CardContent>
        </Card>

        {/* Step 3 — Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-amber-500" />
              3. Schedule Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Publish interval</Label>
              <Select value={String(interval)} onValueChange={(v) => setIntervalMin(Number(v))}>
                <SelectTrigger data-testid="interval-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {intervals.map((i) => <SelectItem key={i.value} value={String(i.value)}>{i.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger data-testid="region-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3 flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="include-faq-toggle">
                <input type="checkbox" checked={includeFaq} onChange={(e) => setIncludeFaq(e.target.checked)} />
                Include FAQ block per article
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="include-image-toggle">
                <input type="checkbox" checked={includeImage} onChange={(e) => setIncludeImage(e.target.checked)} />
                Generate cover image (Nano Banana / Gemini)
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Launch */}
        <Button
          className="w-full h-16 text-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 text-white"
          disabled={launching || titles.length === 0}
          onClick={launch}
          data-testid="launch-monster-btn"
        >
          {launching ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />}
          {launching ? "Launching…" : `Launch Content Monster · ${titles.length} title${titles.length === 1 ? "" : "s"}`}
        </Button>

        {/* Jobs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent jobs</CardTitle>
              <Button variant="ghost" size="sm" onClick={loadJobs} disabled={loadingJobs} data-testid="refresh-jobs-btn">
                <RefreshCw className={`h-4 w-4 ${loadingJobs ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {jobs.length === 0 ? (
              <p className="text-slate-500 text-sm">No scheduler jobs yet for this project.</p>
            ) : (
              <div className="space-y-4">
                {jobs.map((j) => <JobRow key={j.id} job={j} onCancel={() => cancelJob(j.id)} />)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

const statusBadge = (s) => {
  const map = {
    pending:   { cls: "bg-slate-100 text-slate-700",   icon: <Clock className="h-3 w-3" /> },
    running:   { cls: "bg-blue-100 text-blue-700",     icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    published: { cls: "bg-emerald-100 text-emerald-700", icon: <CheckCircle2 className="h-3 w-3" /> },
    failed:    { cls: "bg-red-100 text-red-700",       icon: <AlertCircle className="h-3 w-3" /> },
    cancelled: { cls: "bg-amber-100 text-amber-700",   icon: <X className="h-3 w-3" /> },
  };
  const m = map[s] || map.pending;
  return <Badge className={`${m.cls} gap-1`}>{m.icon}{s}</Badge>;
};

const JobRow = ({ job, onCancel }) => {
  const [expanded, setExpanded] = useState(false);
  const pct = job.title_count > 0 ? (job.published_count / job.title_count) * 100 : 0;
  return (
    <div className="border rounded-lg p-4" data-testid={`job-${job.id}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
            {statusBadge(job.status)}
            <span>{job.published_count} / {job.title_count} published</span>
            <span className="text-xs text-slate-500">· every {job.interval_minutes} min</span>
            {job.category && <span className="text-xs text-slate-500">· {job.category}</span>}
          </div>
          <p className="text-xs text-slate-400 mt-1">{new Date(job.created_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} data-testid={`expand-job-${job.id}`}>
            {expanded ? "Hide" : "Items"}
          </Button>
          {job.status === "running" && (
            <Button variant="outline" size="sm" onClick={onCancel} data-testid={`cancel-job-${job.id}`}>Cancel</Button>
          )}
        </div>
      </div>
      <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      {expanded && (
        <div className="mt-4 max-h-72 overflow-y-auto border-t pt-3 space-y-1">
          {(job.items || []).map((it) => (
            <div key={it.id} className="flex items-center gap-3 text-sm" data-testid={`item-${it.id}`}>
              <span className="text-xs font-mono text-slate-400 w-6">#{it.order + 1}</span>
              <span className="flex-1 min-w-0 truncate">{it.title}</span>
              <span className="text-xs text-slate-400">{new Date(it.scheduled_at).toLocaleString()}</span>
              {statusBadge(it.status)}
              {it.article_slug && (
                <a href={`/article/${it.article_slug}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">view</a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminScheduler;
